import { useCallback, useState } from 'react';

import { Box } from '@mantine/core';

import { AboutIntroOverlay } from '@/components/organisms/AboutIntroOverlay';

const STORAGE_KEY = 'assay-intro-seen-v1';

function shouldSkipIntro(): boolean {
  if (typeof globalThis.window === 'undefined') return true;
  try {
    if (globalThis.localStorage.getItem(STORAGE_KEY)) return true;
  } catch {
    // sandbox / private mode - fall through
  }
  if (typeof globalThis.matchMedia === 'function') {
    try {
      if (globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    } catch {
      // ignore
    }
  }
  return false;
}

function StoryBody({ onReplay }: { onReplay: () => void }) {
  return (
    <>
      <Box
        component="article"
        style={{
          maxWidth: 920,
          margin: '0 auto',
          padding: '84px 32px 64px',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 28,
          }}
        >
          <span
            style={{
              width: 32,
              height: 1,
              background: 'var(--assay-ore)',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontWeight: 500,
              fontSize: '0.6rem',
              lineHeight: 1.4,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--assay-ore)',
            }}
          >
            Relationships, Measured
          </span>
        </div>

        <h1
          style={{
            fontFamily: '"Cormorant Garamond", "Georgia", serif',
            fontWeight: 300,
            fontSize: 'clamp(3.2rem, 7vw, 5.5rem)',
            lineHeight: 1,
            letterSpacing: '-0.02em',
            color: 'var(--assay-text-primary)',
            margin: '0 0 44px',
          }}
        >
          What is{' '}
          <em
            style={{
              fontStyle: 'italic',
              color: 'var(--assay-ore-light)',
              fontWeight: 300,
            }}
          >
            Assay
          </em>
          ?
        </h1>

        <p
          style={{
            fontFamily: '"Cormorant Garamond", "Georgia", serif',
            fontWeight: 300,
            fontStyle: 'italic',
            fontSize: '1.6rem',
            lineHeight: 1.5,
            color: 'var(--assay-text-primary)',
            maxWidth: 720,
            margin: '0 0 56px',
            letterSpacing: '-0.005em',
          }}
        >
          Assay started from a simple frustration: most tools are built for reaching strangers, not
          for keeping the relationships you already have.
        </p>

        <StoryGrid onReplay={onReplay} />
      </Box>

      <CtaStrip />
    </>
  );
}

function StoryGrid({ onReplay }: { onReplay: () => void }) {
  return (
    <div className="about-story-grid">
      <style>{`
        .about-story-grid {
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 56px 64px;
          max-width: 920px;
        }
        @media (max-width: 760px) {
          .about-story-grid { grid-template-columns: 1fr; gap: 12px 0; }
          .about-story-grid .about-marker { margin-top: 22px; }
        }
      `}</style>

      <div className="about-divider-strata">
        <span style={{ background: '#e8b84b', flexGrow: 1.2 }} />
        <span style={{ background: '#c4983a', flexGrow: 1 }} />
        <span style={{ background: '#9a7426', flexGrow: 0.8 }} />
        <span style={{ background: '#6b5c3e', flexGrow: 0.5 }} />
      </div>
      <style>{`
        .about-divider-strata {
          grid-column: 1 / -1;
          display: flex;
          gap: 6px;
          margin: 22px 0 4px;
        }
        .about-divider-strata > span { height: 2px; flex: 1; }
      `}</style>

      <Marker num="01" text="The signal" />
      <Para>
        The best opportunities usually come from <strong>people who already know your name</strong>.
        The problem is that your network goes quiet without you noticing - and by the time someone
        comes to mind, it has been a year.
      </Para>

      <Marker num="02" text="The method" />
      <Para>
        Assay keeps your network warm <strong>on purpose</strong>. Rather than chasing everyone, you
        tend the relationships with the highest signal: the people who trust you, the people close
        to your work, and the people you would hate to lose touch with - and Assay reminds you
        before they go cold.
      </Para>

      <Marker num="03" text="The voice" />
      <Para>
        We built it to make staying in touch feel effortless and human.{' '}
        <strong>
          Sort your contacts, see who is due for a catch-up, and send a message that sounds like
          you.
        </strong>
      </Para>

      <div
        style={{
          gridColumn: '1 / -1',
          margin: '18px 0 6px',
          padding: '36px 0',
          borderTop: '1px solid var(--assay-ore-border)',
          borderBottom: '1px solid var(--assay-ore-border)',
          fontFamily: '"Cormorant Garamond", "Georgia", serif',
          fontWeight: 300,
          fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
          lineHeight: 1.25,
          color: 'var(--assay-text-primary)',
          letterSpacing: '-0.01em',
        }}
      >
        Assay is founder-built and{' '}
        <span style={{ color: 'var(--assay-ore-light)', fontStyle: 'italic' }}>
          opinionated by design
        </span>{' '}
        - less noise, better timing, stronger relationships.
      </div>

      <div
        style={{
          gridColumn: '1 / -1',
          marginTop: 36,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 18,
        }}
      >
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--assay-text-label)',
          }}
        >
          <strong style={{ color: 'var(--assay-ore-light)', fontWeight: 500 }}>
            - The Assay team
          </strong>
        </div>
        <button
          type="button"
          onClick={onReplay}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'transparent',
            border: '1px solid var(--assay-ore-border-hover)',
            color: 'var(--assay-ore-light)',
            padding: '10px 14px',
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            borderRadius: 2,
          }}
        >
          <svg viewBox="0 0 16 16" fill="none" style={{ width: 12, height: 12 }}>
            <path
              d="M2 6.5V2.5L6 6.5M3.2 9a5.5 5.5 0 1 0 .8-3.5"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Replay the story
        </button>
      </div>
    </div>
  );
}

function Marker({ num, text }: { num: string; text: string }) {
  return (
    <div
      className="about-marker"
      style={{
        fontFamily: '"DM Mono", monospace',
        fontWeight: 500,
        fontSize: '0.55rem',
        lineHeight: 1.4,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: 'var(--assay-text-label)',
        paddingTop: 9,
        position: 'relative',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 24,
          height: 1,
          background: 'var(--assay-ore)',
        }}
      />
      <span style={{ color: 'var(--assay-ore)', marginRight: 8 }}>{num}</span>
      {text}
    </div>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        fontFamily: '"DM Mono", monospace',
        fontSize: '0.95rem',
        lineHeight: 1.75,
        color: 'var(--assay-text-body)',
        maxWidth: 580,
      }}
    >
      {children}
    </p>
  );
}

function CtaStrip() {
  return (
    <section
      style={{
        maxWidth: 920,
        margin: '60px auto 0',
        padding: '0 32px',
      }}
    >
      <div
        style={{
          border: '1px solid var(--assay-ore-border)',
          borderRadius: 2,
          padding: '32px 36px',
          background: 'linear-gradient(120deg, rgba(196,152,58,0.06) 0%, transparent 70%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: '"Cormorant Garamond", "Georgia", serif',
              fontWeight: 300,
              fontSize: '1.7rem',
              color: 'var(--assay-text-primary)',
              margin: '0 0 6px',
              letterSpacing: '-0.01em',
            }}
          >
            Ready to stop losing touch?
          </h2>
          <p
            style={{
              margin: 0,
              fontFamily: '"DM Mono", monospace',
              fontSize: '0.75rem',
              color: 'var(--assay-text-label)',
              lineHeight: 1.6,
              maxWidth: 460,
            }}
          >
            Start free, import your network, and let Assay remind you who to reconnect with - no
            cold templates, no AI slop.
          </p>
        </div>
        <a
          href="/signup"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--assay-ore)',
            color: '#14110a',
            padding: '12px 18px',
            borderRadius: 2,
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            border: '1px solid var(--assay-ore)',
            whiteSpace: 'nowrap',
            textDecoration: 'none',
          }}
        >
          Start for free
          <span style={{ fontSize: '1rem', lineHeight: 0.6 }}>›</span>
        </a>
      </div>
    </section>
  );
}

export function AboutStory() {
  const [showIntro, setShowIntro] = useState(() => !shouldSkipIntro());

  const handleEnd = useCallback(() => {
    try {
      globalThis.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    setShowIntro(false);
  }, []);

  const handleReplay = useCallback(() => {
    try {
      globalThis.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setShowIntro(true);
  }, []);

  if (showIntro) {
    return (
      <Box style={{ padding: '32px 32px 64px' }}>
        <AboutIntroOverlay onEnd={handleEnd} />
      </Box>
    );
  }

  return (
    <Box
      style={{
        animation: 'assay-fade-up 0.7s ease both',
      }}
    >
      <StoryBody onReplay={handleReplay} />
    </Box>
  );
}
